// The signin crypto/auth flow, extracted as a plain async function (no Redux Toolkit) so the
// login bundle can run it without pulling @reduxjs/toolkit/immer/react-redux into its first-paint
// chunk. The main app's RTK thunk (slices/auth) and the login store (pages/Login) both call this
// single source of truth, so the security-critical flow is never duplicated.
//
// -------------------------------------------------------------------------
// REMEMBER: We have a goal that nobody can brute force account credentials.
//           Fingerprint is easy to see if you forcefuly grab user`s device.
//           Picture of 10x10 with 16 cells has ~ 2B variations 100*4*3**14.
//           Password of 4 numbers has ~ 10K options but most could be easy.
// -------------------------------------------------------------------------
// Here are the rules we must follow:
// 1) No one on internet should know userId + accountId used for logging in.
// 2) Produced keys must be created on-the-fly or use something from server.
// 3) Original password must be transformed before being sent to the server.
// -------------------------------------------------------------------------
import { postToApp } from 'auth/appChannel';

export const signinRequest = async (authAPI, credentials) => {
  // Submit-time crypto/fingerprint deps are loaded on demand so they stay out of the login
  // first-paint chunk: thumbmark, uuid-by-string (+ js-md5/js-sha1) and json-stable-stringify.
  // auth/main.js warms them in idle once the login UI paints, so the first submit doesn't wait
  // on the network.
  const [{ getFingerprints }, { getUuid }, { sha512 }] = await Promise.all([
    import('utils/fingerprint'),
    import('utils/hashUuid'),
    import('utils/crypto'),
  ]);

  const picture = credentials.picture;
  const rawPassword = credentials.password;
  const currentToken = credentials.currentToken;
  const fingerprints = await getFingerprints();
  if (!rawPassword || !fingerprints) {
    throw new Error('No credentials provided!');
  }

  const fingerprintId = getUuid({ fingerprints });
  const fingerprintSalt = await authAPI.generateSalt({ fingerprintId });

  const userId = getUuid({ fingerprintId, fingerprintSalt, fingerprints, rawPassword });

  let userSalt;
  try {
    userSalt = await authAPI.generateSalt({ fingerprintId, userId, currentToken });
  } catch (error) {
    if (error.message?.includes('401') && window.In6to.isAuth) {
      // Current session token is already dead — tell the app to hard-signout. Goes over the private
      // channel the app handed us (see auth/appChannel); no-op if we somehow have no port.
      postToApp({ type: 'panic' });
    }
    throw error;
  }

  const password = await sha512({
    fingerprintId,
    fingerprintSalt,
    fingerprints,
    rawPassword,
    userId,
    userSalt,
  });

  let authResult;
  if (picture) {
    const masterAccountId = getUuid({ picture });
    const masterAccountSalt = await authAPI.generateSalt({
      fingerprintId,
      masterAccountId,
      userId,
    });

    const masterUserId = getUuid({
      accountId: masterAccountId,
      accountSalt: masterAccountSalt,
      picture,
      rawPassword,
    });
    const masterUserSalt = await authAPI.generateSalt({
      fingerprintId,
      masterAccountId,
      masterUserId,
      userId,
    });
    const masterPassword = await sha512({
      accountId: masterAccountId,
      accountSalt: masterAccountSalt,
      picture,
      rawPassword,
      userId: masterUserId,
      userSalt: masterUserSalt,
    });

    // TODO: use multiple public+private keys for encryption, then save public part
    authResult = await authAPI.create({
      masterUserId,
      // masterKey: keyPair.publicKey, // TODO: attach public key to master user
      masterPassword: masterPassword.slice(0, 64),
      id: userId,
      password: password.slice(0, 64),
    });
  } else {
    // TODO: use multiple public+private keys for encryption, then save public part
    authResult = await authAPI.create({
      id: userId,
      // key: keyPair.publicKey, // TODO: attach public key to user
      password: password.slice(0, 64),
    });
  }

  const { masterUserHash, navigationState, /* publicKeys, */ ...user } = authResult;

  if (!masterUserHash) {
    return { error: true };
  }

  const encryptionKey = await sha512({ masterUserHash, rawPassword }); // TODO: use multiple public+private keys for encryption instead

  // navigationState: client-encrypted last-page blob ({ id, data }) returned by the login
  // endpoint, so the app can restore the last page without an extra request.
  return { encryptionKey, user, navigationState };
};
